/**
 * MODULE: domain/pages/frontend/utils/databaseBoardModel.ts
 * PURPOSE: Group database rows into board columns by a select field
 */

import type { DatabaseRow, FieldDefinition, PageView } from '../../contracts/pages.types';
import { fieldMatchesSearch } from './databaseFieldUtils';

export const GROUP_BY_CONFIG_KEY = 'group_by_field_id';

export type BoardColumn = {
  id: string;
  label: string;
  optionId: string | null;
};

export function getGroupByFieldId(view: PageView | undefined): string | undefined {
  const value = view?.sort_config?.[GROUP_BY_CONFIG_KEY];
  return typeof value === 'string' ? value : undefined;
}

export function findTitleField(fields: FieldDefinition[]): FieldDefinition | undefined {
  return fields.find((f) => f.id === 'name') ?? fields.find((f) => f.field_type === 'text');
}

export function findSelectFields(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => f.field_type === 'select');
}

export function findGroupField(
  fields: FieldDefinition[],
  groupByFieldId?: string
): FieldDefinition | undefined {
  if (groupByFieldId) {
    const match = fields.find((f) => f.id === groupByFieldId && f.field_type === 'select');
    if (match) return match;
  }
  return findSelectFields(fields)[0];
}

export function buildBoardColumns(groupField: FieldDefinition | undefined): BoardColumn[] {
  if (!groupField?.options?.length) {
    return [{ id: '__all', label: 'All rows', optionId: null }];
  }

  return [
    { id: '__none', label: 'No status', optionId: null },
    ...groupField.options.map((option) => ({
      id: option.id,
      label: option.name,
      optionId: option.id,
    })),
  ];
}

export function getRowGroupValue(row: DatabaseRow, groupField: FieldDefinition | undefined): string | null {
  if (!groupField) return null;
  const raw = row.properties?.[groupField.id];
  if (raw === null || raw === undefined || raw === '') return null;
  const value = String(raw);
  const valid = groupField.options?.some((o) => o.id === value);
  return valid ? value : null;
}

export function groupRowsIntoColumns(
  rows: DatabaseRow[],
  groupField: FieldDefinition | undefined,
  columns: BoardColumn[]
): Record<string, DatabaseRow[]> {
  const grouped: Record<string, DatabaseRow[]> = {};
  for (const column of columns) {
    grouped[column.id] = [];
  }

  if (!groupField?.options?.length) {
    grouped[columns[0]?.id ?? '__all'] = [...rows];
    return grouped;
  }

  for (const row of rows) {
    const value = getRowGroupValue(row, groupField);
    const columnId = value ?? '__none';
    if (!grouped[columnId]) grouped[columnId] = [];
    grouped[columnId].push(row);
  }

  return grouped;
}

export function filterDatabaseRows(
  rows: DatabaseRow[],
  fields: FieldDefinition[],
  query: string
): DatabaseRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    fields.some((field) => fieldMatchesSearch(field, row.properties?.[field.id], q))
  );
}

export function getRowTitle(row: DatabaseRow, titleField: FieldDefinition | undefined): string {
  if (!titleField) return 'Untitled';
  const value = row.properties?.[titleField.id];
  if (value === null || value === undefined || value === '') return 'Untitled';
  return String(value);
}
